import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StorefrontShell } from '@/components/store/StorefrontShell';
import { ProductGrid } from '@/components/store/ProductGrid';
import { TrustStrip } from '@/components/store/TrustStrip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { storeCopy } from '@/content/store-copy';
import { useProducts } from '@/features/products/queries';
import {
  CATEGORY_LABEL,
  PRODUCT_CATEGORIES,
  isCategory,
  type Product,
  type ProductCategory,
} from '@/features/products/types';
import { cn } from '@/lib/utils';

type Sort = 'featured' | 'newest' | 'priceAsc' | 'priceDesc';

const SORT_LABEL: Record<Sort, string> = {
  featured: storeCopy.shop.sort.featured,
  newest: storeCopy.shop.sort.newest,
  priceAsc: storeCopy.shop.sort.priceAsc,
  priceDesc: storeCopy.shop.sort.priceDesc,
};

function sortProducts(products: Product[], sort: Sort): Product[] {
  const copy = [...products];

  switch (sort) {
    case 'newest':
      return copy.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    case 'priceAsc':
      return copy.sort((a, b) => a.price - b.price);
    case 'priceDesc':
      return copy.sort((a, b) => b.price - a.price);
    default:
      return copy.sort((a, b) => a.sortOrder - b.sortOrder);
  }
}

function ShopContent() {
  const { data: products, isPending, isError } = useProducts();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState<Sort>('featured');

  const raw = params.get('category');
  const active: ProductCategory | null = isCategory(raw) ? raw : null;

  const visible = useMemo(() => {
    const list = (products ?? []).filter((product) => !active || product.category === active);
    return sortProducts(list, sort);
  }, [products, active, sort]);

  const counts = useMemo(() => {
    const map = new Map<ProductCategory, number>();
    for (const product of products ?? []) {
      map.set(product.category, (map.get(product.category) ?? 0) + 1);
    }
    return map;
  }, [products]);

  const setCategory = (category: ProductCategory | null) => {
    const next = new URLSearchParams(params);
    if (category) next.set('category', category);
    else next.delete('category');
    setParams(next, { replace: true });
  };

  const chips: Array<{ value: ProductCategory | null; label: string; count: number }> = [
    { value: null, label: storeCopy.shop.all, count: products?.length ?? 0 },
    ...PRODUCT_CATEGORIES.filter((category) => (counts.get(category) ?? 0) > 0).map((category) => ({
      value: category,
      label: CATEGORY_LABEL[category],
      count: counts.get(category) ?? 0,
    })),
  ];

  return (
    <main className="bg-night">
      <div className="border-b border-brand-line bg-[linear-gradient(180deg,#080608,#100b0e)] px-[clamp(20px,4vw,64px)] pt-12 pb-8 max-[560px]:px-4 max-[560px]:pt-8">
        <div className="mx-auto max-w-[1420px]">
          <p className="text-[0.78rem] tracking-[0.22em] text-brand-muted uppercase">
            {storeCopy.shop.title}
          </p>
          <h1 className="mt-[6px] font-serif text-[clamp(2.4rem,4.4vw,3.6rem)] leading-none">
            {active ? CATEGORY_LABEL[active] : storeCopy.shop.subtitle}
          </h1>
          <p className="mt-3 text-sm text-brand-muted" aria-live="polite">
            {storeCopy.shop.count(visible.length)}
          </p>
        </div>
      </div>

      <section className="px-[clamp(20px,4vw,64px)] py-8 max-[560px]:px-4 max-[560px]:py-6">
        <div className="mx-auto max-w-[1420px]">
          <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by collection">
              {chips.map((chip) => {
                const selected = chip.value === active;
                return (
                  <button
                    key={chip.value ?? 'all'}
                    type="button"
                    onClick={() => setCategory(chip.value)}
                    aria-pressed={selected}
                    className={cn(
                      'border px-4 py-2 text-[0.82rem] transition-colors',
                      selected
                        ? 'border-gold bg-gold text-night'
                        : 'border-brand-line text-brand-muted hover:border-gold hover:text-cream',
                    )}
                  >
                    {chip.label}
                    <span className={cn('ml-1.5', selected ? 'text-night/70' : 'text-brand-muted/70')}>
                      {chip.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-brand-muted">{storeCopy.shop.sortLabel}</span>
              <Select value={sort} onValueChange={(value) => setSort(value as Sort)}>
                <SelectTrigger className="h-10 w-44" aria-label={storeCopy.shop.sortLabel}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SORT_LABEL) as Sort[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {SORT_LABEL[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ProductGrid
            products={visible}
            isPending={isPending}
            isError={isError}
            emptyMessage={storeCopy.shop.empty}
          />

          {!isPending && !isError && visible.length === 0 && active && (
            <button
              type="button"
              onClick={() => setCategory(null)}
              className="mt-5 text-sm text-gold-soft underline underline-offset-4 hover:text-cream"
            >
              {storeCopy.shop.clear}
            </button>
          )}
        </div>
      </section>

      <TrustStrip />
    </main>
  );
}

export function ShopPage() {
  return (
    <StorefrontShell>
      <ShopContent />
    </StorefrontShell>
  );
}
